import React from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ChecklistItem {
  id: string;
  label: string;
  status: 'complete' | 'incomplete' | 'optional';
  required: boolean;
  message?: string;
}

interface SetupCompletionChecklistProps {
  items: ChecklistItem[];
  compact?: boolean;
}

export function SetupCompletionChecklist({ items, compact = false }: SetupCompletionChecklistProps) {
  const requiredComplete = items.filter(i => i.required && i.status === 'complete').length;
  const requiredTotal = items.filter(i => i.required).length;
  const allRequiredComplete = requiredComplete === requiredTotal;
  
  return (
    <div className={cn(
      "border rounded-lg",
      allRequiredComplete ? "border-primary/30 bg-primary/5" : "border-amber-500/30 bg-amber-500/5"
    )}>
      <div className={cn(
        "flex items-center justify-between px-4 py-2 border-b",
        allRequiredComplete ? "border-primary/20" : "border-amber-500/20"
      )}>
        <h4 className="font-semibold text-sm">Setup Completion Checklist</h4>
        <span className={cn(
          "text-xs font-medium px-2 py-0.5 rounded-full",
          allRequiredComplete 
            ? "bg-primary/10 text-primary" 
            : "bg-amber-500/10 text-amber-600"
        )}>
          {requiredComplete}/{requiredTotal} required
        </span>
      </div>
      
      <div className={cn("space-y-1", compact ? "p-2" : "p-3")}>
        {items.map((item) => (
          <div 
            key={item.id}
            className={cn(
              "flex items-center gap-2 text-sm",
              compact ? "py-0.5" : "py-1"
            )}
          >
            {item.status === 'complete' ? (
              <CheckCircle className="h-4 w-4 text-primary shrink-0" />
            ) : item.status === 'incomplete' && item.required ? (
              <XCircle className="h-4 w-4 text-destructive shrink-0" />
            ) : (
              <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            
            <span className={cn(
              item.status === 'complete' 
                ? "text-foreground" 
                : item.required 
                  ? "text-destructive" 
                  : "text-muted-foreground"
            )}>
              {item.label}
              {!item.required && (
                <span className="text-xs text-muted-foreground ml-1">(optional)</span>
              )}
            </span>
            
            {item.message && item.status !== 'complete' && (
              <span className="text-xs text-muted-foreground ml-auto">
                {item.message}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
